import { isJsonStr } from "../general-utils/generalUtils"
import {setDisplayProjects, setSeasonalFilterRdx, setSrcFilters} from "../store/GenericReducer"
import { enToHeActivityField } from "../../utils/activityFieldsDict"
import { enToHePopulation } from "../../utils/populationDict"


// New function to build filters from CRM projects (Organization only)
const buildFiltersFromProjects = (projects, store, dispatch) => {
    // Filter only Organization projects
    const orgProjects = projects || [];

    let city = [];
    let area = [];
    let activityField = [];
    let population = [];

    orgProjects.forEach(project => {
        // City filter - איפה תרצה להתנדב?
        if (project.address?.city?.name) {
            city.push({
                cityID: project.address.city._id,
                cityTitle: project.address.city.name,
                // Include area reference for city->area relationship
                areaID: project.address.city.area?._id,
                areaName: project.address.city.area?.name
            });
        }

        // Area filter - extracted from city.area
        if (project.address?.city?.area?.name) {
            area.push({
                areaID: project.address.city.area._id,
                areaTitle: project.address.city.area.name
            });
        }

        // Activity Field filter - איך תרצו להתנדב?
        if (project.attributes?.activityField) {
            const fieldValue = project.attributes.activityField;
            activityField.push({
                id: fieldValue,
                name: fieldValue,
                title: enToHeActivityField[fieldValue] || fieldValue
            });
        }

        // Population filter - למי תרצו לעזור?
        if (project.attributes?.population) {
            const popValue = project.attributes.population;
            population.push({
                id: popValue,
                name: popValue,
                title: enToHePopulation[popValue] || popValue
            });
        }
    });

    // Get unique values
    const uniqueCity = getArrayUniqueValues(city, 'cityID');
    const uniqueArea = getArrayUniqueValues(area, 'areaID');
    const uniqueActivityField = getArrayUniqueValues(activityField, 'id');
    const uniquePopulation = getArrayUniqueValues(population, 'id');

    const finalObj = {
        city: uniqueCity,           // איפה תרצה להתנדב? (cities for autocomplete)
        area: uniqueArea,           // איפה תרצה להתנדב? (areas for dropdown)
        volunteerFields: uniqueActivityField,  // איך תרצו להתנדב?
        population: uniquePopulation  // למי תרצו לעזור?
    };

    // Dispatch filters to Redux store
    if (dispatch) {
        dispatch(setSrcFilters(finalObj));
    }

    return finalObj;
};

const getArrayUniqueValues = (array, id) => {
    let uniqueArray = array.filter((obj, index, self) => 
        index === self.findIndex((t) => (t[id] === obj[id]))
    )
    uniqueArray.sort((a, b) => a[id] - b[id])
    return uniqueArray
}

const isEmpty = (obj) => {
    for (const prop in obj) {
      if (Object.hasOwn(obj, prop)) {
        return false;
      }
    }
  
    return true;
}

// New filterProjects function for CRM data structure
const filterProjects = (store, dispatch) => {
    // Get only Organization projects
    const allProjects = store.allProjects || [];

    // Get filter choices
    const filterChoices = store.filterChoices || {};

    // Check if any filter is selected
    let hasActiveFilter = false;
    Object.values(filterChoices).forEach(filter => {
        if (filter?.choice) {
            hasActiveFilter = true;
        }
    });

    // If no filters selected, show all Organization projects (sorted)
    if (!hasActiveFilter) {
        const sortedProjects = sortProjectsByDisplayOrder(allProjects);
        dispatch(setDisplayProjects(sortedProjects));
        return;
    }

    // Get location filter choice
    const locationChoice = filterChoices[1]?.choice;
    const isCityFilter = locationChoice?.cityID ? true : false;
    const selectedCityID = locationChoice?.cityID;
    const selectedAreaID = locationChoice?.areaID;

    // Filter projects based on selected choices
    let filteredProjects = allProjects.filter(project => {
        let matchLocation = true;
        let matchActivityField = true;
        let matchPopulation = true;

        // Location filter (filterTypeID: 1) - supports both city and area
        if (locationChoice) {
            if (isCityFilter) {
                // City filter - include projects from same city OR same area
                const projectCityID = project.address?.city?._id;
                const projectAreaID = project.address?.city?.area?._id;
                matchLocation = projectCityID === selectedCityID || projectAreaID === selectedAreaID;
            } else if (selectedAreaID) {
                // Area filter - match projects where city belongs to this area
                matchLocation = project.address?.city?.area?._id === selectedAreaID;
            }
        }

        // Activity Field / VolunteerFields filter (filterTypeID: 6)
        const activityFieldChoice = filterChoices[6]?.choice;
        if (activityFieldChoice) {
            matchActivityField = project.attributes?.activityField === activityFieldChoice.id;
        }

        // Population filter (filterTypeID: 7)
        const populationChoice = filterChoices[7]?.choice;
        if (populationChoice) {
            matchPopulation = project.attributes?.population === populationChoice.id;
        }

        // Project must match ALL selected filters (AND logic)
        return matchLocation && matchActivityField && matchPopulation;
    });

    // Apply appropriate sorting based on filter type
    let sortedProjects;
    if (isCityFilter) {
        // City filter: use city-specific sorting (same city first, then same area, then fully assigned)
        sortedProjects = sortProjectsForCityFilter(filteredProjects, selectedCityID, selectedAreaID);
    } else {
        // Default sorting for area, activity, population filters
        sortedProjects = sortProjectsByDisplayOrder(filteredProjects);
    }

    // console.log('filterProjects - Filtered results:', sortedProjects.length, 'projects');
    dispatch(setDisplayProjects(sortedProjects));
}

const findCommonValues = (...arrays) => {
    const nonEmptyArrays = arrays.filter(arr => arr.length > 0)
    if (nonEmptyArrays.length === 0) return [];

    return nonEmptyArrays[0].filter(value =>
        nonEmptyArrays.every(arr => arr.includes(value))
    );
}

// Good Deeds Day date - TODO: Get from CRM later
const GOOD_DEEDS_DAY = new Date(2026, 2, 10); // March 10, 2026 (month is 0-indexed)

// Helper to check if two dates are the same day
const isSameDay = (date1, date2) => {
    if (!date1 || !date2) return false;
    const d1 = new Date(date1);
    const d2 = new Date(date2);
    return d1.getFullYear() === d2.getFullYear() &&
           d1.getMonth() === d2.getMonth() &&
           d1.getDate() === d2.getDate();
};

// Categorize project and assign display order category
// Categories:
// 1 = Top project (isTopProject=true) with partial/no volunteers, ordered by project date
// 2 = Regular project on Good Deeds Day, ordered by creation date
// 3 = Regular project on other dates, ordered by project date
// 4 = Fully assigned project, ordered by project date
const categorizeProject = (project) => {
    const isTopProject = project.attributes?.isTopProject === true;
    const assignmentStatus = project.assignmentStatus; // "fully", "partially", "noVolunteers"
    const projectDate = project.date ? new Date(project.date) : null;
    const isGoodDeedsDay = isSameDay(projectDate, GOOD_DEEDS_DAY);
    const isFullyAssigned = assignmentStatus === 'fully';

    let category;

    if (isFullyAssigned) {
        // Category 4: Fully assigned projects (lowest priority)
        category = 4;
    } else if (isTopProject && (assignmentStatus === 'partially' || assignmentStatus === 'noVolunteers')) {
        // Category 1: Top projects with available spots (highest priority)
        category = 1;
    } else if (!isTopProject && isGoodDeedsDay) {
        // Category 2: Regular projects on Good Deeds Day
        category = 2;
    } else {
        // Category 3: Regular projects on other dates
        category = 3;
    }

    // Return project with category and filter type attached
    return {
        ...project,
        _displayCategory: category,
        _filterType: 'default'
    };
};

// Sort projects according to display order
const sortProjectsByDisplayOrder = (projects) => {
    return projects
        .map(categorizeProject)
        .sort((a, b) => {
            // First, sort by category
            if (a._displayCategory !== b._displayCategory) {
                return a._displayCategory - b._displayCategory;
            }

            // Within the same category, apply specific sorting
            const dateA = a.date ? new Date(a.date) : new Date(0);
            const dateB = b.date ? new Date(b.date) : new Date(0);
            const createdA = a.createdAt ? new Date(a.createdAt) : new Date(0);
            const createdB = b.createdAt ? new Date(b.createdAt) : new Date(0);

            switch (a._displayCategory) {
                case 1:
                    // Category 1: Order by project date (ascending - earlier dates first)
                    return dateA - dateB;
                case 2:
                    // Category 2: Order by creation date (ascending - older first)
                    return createdA - createdB;
                case 3:
                    // Category 3: Order by project date (ascending)
                    return dateA - dateB;
                case 4:
                    // Category 4: Order by project date (ascending)
                    return dateA - dateB;
                default:
                    return 0;
            }
        });
};

// Categorize project for city filter with location priority
// Categories for city filter:
// 1 = Same city, Top project with partial/no volunteers
// 2 = Same city, Regular project on Good Deeds Day
// 3 = Same city, Regular project on other dates
// 4 = Same area (different city), Top project with partial/no volunteers
// 5 = Same area (different city), Regular project on Good Deeds Day
// 6 = Same area (different city), Regular project on other dates
// 7 = Same city, Fully assigned projects
// 8 = Same area (different city), Fully assigned projects
const categorizeProjectForCity = (project, selectedCityID, selectedAreaID) => {
    const isTopProject = project.attributes?.isTopProject === true;
    const assignmentStatus = project.assignmentStatus;
    const projectDate = project.date ? new Date(project.date) : null;
    const isGoodDeedsDay = isSameDay(projectDate, GOOD_DEEDS_DAY);
    const isFullyAssigned = assignmentStatus === 'fully';

    const projectCityID = project.address?.city?._id;
    const projectAreaID = project.address?.city?.area?._id;

    const isSameCity = projectCityID === selectedCityID;
    const isSameArea = projectAreaID === selectedAreaID && !isSameCity;

    let category;

    if (isSameCity) {
        // Same city projects
        if (isFullyAssigned) {
            category = 7; // Same city, Fully assigned
        } else if (isTopProject && (assignmentStatus === 'partially' || assignmentStatus === 'noVolunteers')) {
            category = 1; // Same city, Top project
        } else if (isGoodDeedsDay) {
            category = 2; // Same city, Good Deeds Day
        } else {
            category = 3; // Same city, Regular
        }
    } else if (isSameArea) {
        // Same area, different city
        if (isFullyAssigned) {
            category = 8; // Same area, Fully assigned
        } else if (isTopProject && (assignmentStatus === 'partially' || assignmentStatus === 'noVolunteers')) {
            category = 4; // Same area, Top project
        } else if (isGoodDeedsDay) {
            category = 5; // Same area, Good Deeds Day
        } else {
            category = 6; // Same area, Regular
        }
    } else {
        // Different area - shouldn't happen with city filter, but fallback
        category = 8;
    }

    return {
        ...project,
        _displayCategory: category,
        _filterType: 'city'
    };
};

// Sort projects for city filter - includes same area projects
const sortProjectsForCityFilter = (projects, selectedCityID, selectedAreaID) => {
    return projects
        .map(project => categorizeProjectForCity(project, selectedCityID, selectedAreaID))
        .sort((a, b) => {
            // First, sort by category
            if (a._displayCategory !== b._displayCategory) {
                return a._displayCategory - b._displayCategory;
            }

            // Within the same category, apply specific sorting
            const dateA = a.date ? new Date(a.date) : new Date(0);
            const dateB = b.date ? new Date(b.date) : new Date(0);
            const createdA = a.createdAt ? new Date(a.createdAt) : new Date(0);
            const createdB = b.createdAt ? new Date(b.createdAt) : new Date(0);

            // Categories 1, 4 (Top projects): order by project date
            // Categories 2, 5 (Good Deeds Day): order by creation date
            // Categories 3, 6, 7, 8 (Regular/Fully): order by project date
            switch (a._displayCategory) {
                case 1:
                case 4:
                    return dateA - dateB;
                case 2:
                case 5:
                    return createdA - createdB;
                case 3:
                case 6:
                case 7:
                case 8:
                    return dateA - dateB;
                default:
                    return 0;
            }
        });
};

export { buildFiltersFromProjects, getArrayUniqueValues, isEmpty, filterProjects, sortProjectsByDisplayOrder, sortProjectsForCityFilter, categorizeProject, categorizeProjectForCity, GOOD_DEEDS_DAY }